import { useIntl } from "react-intl";
import { useMemo, useState, useEffect, useCallback } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useDoctors } from "./hooks";

import {
  Breadcrumb,
  Container,
  Loading,
  Modal,
  Pagination,
  RenderByCondition,
} from "components";

import { ListPageTitle } from "common";

import { TDoctorsFiltersFormValue } from "./Doctors.types";
import { DoctorCard } from "./DoctorsCard";
import { DoctorsModalContent } from "./DoctorsModalContent";
import { DoctorsFilters } from "./DoctorsFilters";

import "./Doctors.scss";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { languages_enum } from "redux/actions/globalSlice";

export interface showDoctorsModalProps {
  show: boolean;
  value: number;
  index: number;
}

export function Doctors() {
  const navigate = useNavigate();
  const { page } = useParams();
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();

  const language = useSelector((state: RootState) => state.global.language);
  const prefix = language === languages_enum.GEORGIAN ? "" : `/${language}`;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [doctorsFiltersFormValue, setDoctorsFiltersFormValue] = useState<any>({
    speciality: null,
    branch: null,
    name: "",
    order: null,
  });

  useEffect(() => {
    setDoctorsFiltersFormValue({
      speciality: searchParams.get("speciality")
        ? JSON.parse(searchParams.get("speciality")!)
        : null,
      branch: searchParams.get("branch")
        ? JSON.parse(searchParams.get("branch")!)
        : null,
      name: searchParams.get("name") ?? "",
      order: searchParams.get("order")
        ? JSON.parse(searchParams.get("order")!)
        : null,
    });
  }, [searchParams]);

  const { isLoading, doctors, PAGE_SIZE, maxPages } = useDoctors({
    currentPage,
    doctorsFiltersFormValue,
  });

  const [pressedNext, setPressedNext] = useState(false);
  const [pressedPrev, setPressedPrev] = useState(false);

  async function asyncShowNextPageModal() {
    try {
      let result = await doctors;
      if (result && result.length > 0) {
        navigate(
          `${prefix}/doctors/${currentPage}/doctor/${result[0]?.id}/${result[0]?.name}`
        );
        setPressedNext(false);
      }
    } catch (error) {
      console.error("Error navigating to doctors:", error);
    }
  }

  async function asyncShowPrevPageModal() {
    try {
      let result = await doctors;
      if (result && result.length > 0) {
        navigate(
          `${prefix}/doctors/${currentPage}/doctor/${result[result.length - 1]?.id}/${result[result.length - 1]?.name}`
        );
        setPressedPrev(false);
      }
    } catch (error) {
      console.error("Error navigating to doctors:", error);
    }
  }

  useEffect(() => {
    if (pressedNext) {
      asyncShowNextPageModal();
    }
    if (pressedPrev) {
      asyncShowPrevPageModal();
    }
  }, [pressedNext, currentPage, doctors, pressedPrev]);

  const [currentDoctorId, setCurrentDoctorId] = useState<string | number>();

  const currentDoctorIndex = useMemo(() => {
    if (currentDoctorId) {
      return doctors?.findIndex((doctor: any) => {
        return String(doctor?.id) === String(currentDoctorId);
      });
    }
  }, [doctors, currentDoctorId]);

  useEffect(() => {
    if (Number(page) <= maxPages && !pressedNext && !pressedPrev) {
      setCurrentPage(Number(page));
    }
  }, [page, maxPages]);

  const navigateCurrentDoctor = useCallback(
    (path: string) => {
      navigate(`${prefix}/doctors/${currentPage}/doctor/${path}`);
    },
    [currentPage, prefix]
  );

  const onDoctorFiltersChange = (e: TDoctorsFiltersFormValue): void => {
    setDoctorsFiltersFormValue(e);

    const params = new URLSearchParams();

    if (e.branch) {
      params.set("branch", JSON.stringify(e.branch));
    }
    if (e.speciality) {
      params.set("speciality", JSON.stringify(e.speciality));
    }
    if (e.order) {
      params.set("order", JSON.stringify(e.order));
    }
    if (e.name) {
      params.set("name", e.name);
    }

    navigate(`${prefix}/doctors/1?${params.toString()}`);
  };

  return (
    <>
      <Container className="doctors">
        <ListPageTitle>
          {formatMessage({ id: "doctors.title", defaultMessage: "_title_" })}
        </ListPageTitle>
        <div className="doctors-form">
          <Breadcrumb
            className="doctors-form__breadcrumb"
            links={[
              {
                label: formatMessage({
                  id: "main.breadcrumb",
                  defaultMessage: "_main.breadcrumb_",
                }),
                value: `${prefix}/`,
              },
              {
                label: formatMessage({
                  id: "doctors.breadcrumb",
                  defaultMessage: "_doctors.breadcrumb_",
                }),
                value: `${prefix}/doctors/${currentPage}`,
              },
            ]}
          />
          <DoctorsFilters onChange={onDoctorFiltersChange} />
        </div>
        <div className="doctors-content">
          <RenderByCondition
            showElement={!isLoading}
            defaultElement={<Loading />}
            element={doctors?.map((doctor: any) =>
              doctor ? (
                <DoctorCard
                  gender={doctor?.gender}
                  key={doctor?.id}
                  image={doctor?.photo}
                  title={doctor?.name}
                  href={`${prefix}/doctors/1/doctor/${doctor?.id}/${doctor?.name}`}
                  subtitle={doctor?.specialties
                    ?.map((s: any) => s?.title)
                    ?.join(", ")}
                  location={doctor?.branches
                    ?.map((b: any) => b?.title)
                    ?.join(", ")}
                  setShowDoctorsModal={() =>
                    navigateCurrentDoctor(`${doctor?.id}/${doctor?.name}`)
                  }
                />
              ) : null
            )}
          />
        </div>
        <Pagination
          className="doctors-pagination"
          maxPages={maxPages}
          pageSize={PAGE_SIZE}
          currentPage={currentPage}
          onChange={(page: number) => {
            navigate(`${prefix}/doctors/${page}?${searchParams.toString()}`);
          }}
        />
      </Container>

      {/* Modal */}
      <Routes>
        <Route
          path="doctor/:id/:name"
          element={
            <Modal
              show={true}
              onClose={() => {
                navigate(`${prefix}/doctors/${currentPage}`);
              }}
              next={() => {
                if (currentDoctorId == doctors[doctors.length - 1]?.id) {
                  if (currentPage !== maxPages) {
                    setCurrentPage(currentPage + 1);
                    setPressedNext(true);
                  }
                } else if (currentDoctorIndex !== -1) {
                  navigateCurrentDoctor(
                    `${doctors[currentDoctorIndex + 1]?.id}/${doctors[currentDoctorIndex + 1]?.name}`
                  );
                }
              }}
              prev={() => {
                if (currentDoctorId == doctors[0]?.id) {
                  if (currentPage !== 1) {
                    setCurrentPage(currentPage - 1);
                    setPressedPrev(true);
                  }
                } else if (currentDoctorIndex !== -1) {
                  navigateCurrentDoctor(
                    `${doctors[currentDoctorIndex - 1]?.id}/${doctors[currentDoctorIndex - 1]?.name}`
                  );
                }
              }}
            >
              <DoctorsModalContent setCurrentDoctorId={setCurrentDoctorId} />
            </Modal>
          }
        />
      </Routes>
    </>
  );
}
